import React from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingRow,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@coval/ui"
import { Settings2 } from "lucide-react"

import type { PersonaMetadata } from "@/types/api/Persona"
import { SimulatorType } from "@/types/api/SimulatorType"
import type {
  OpenAIRealtimePersonaConfig,
  OpenAIRealtimeVoice,
} from "@/types/ModelConfigs/OpenAIRealtimeModelConfigs"
import type { TextPersonaConfig } from "@/types/ModelConfigs/TextModelConfigs"
import type { VoicePersonaConfig } from "@/types/ModelConfigs/VoiceModelConfigs"
import type { PersonaConfig } from "@/types/PersonaConfig"

import type { PersonaFormController } from "../hooks/usePersonaFormController"
import { MultiPhoneConfig } from "./MultiPhoneConfig"
import { PersonaInitializationParametersField } from "./PersonaInitializationParametersField"
import { PersonaTextConfigurationField } from "./PersonaTextConfigurationField"

const HOLD_MUSIC_TIMEOUT_DEFAULT_SECONDS = 15
const HOLD_MUSIC_TIMEOUT_MIN_SECONDS = 5
const HOLD_MUSIC_TIMEOUT_MAX_SECONDS = 300

const CALLER_PHONE_NUMBER_DOCS_URL =
  "https://docs.coval.ai/concepts/personas/overview#caller-phone-number"

const INTERRUPTION_RATE_OPTIONS: Array<{
  value: NonNullable<VoicePersonaConfig["interruption_rate"]>
  label: string
}> = [
  { value: "NONE", label: "None" },
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
]

const OPENAI_REALTIME_VOICES: Array<{
  value: OpenAIRealtimeVoice
  label: string
  description: string
}> = [
  { value: "alloy", label: "Alloy", description: "Neutral, balanced" },
  { value: "ash", label: "Ash", description: "Authoritative, male" },
  { value: "ballad", label: "Ballad", description: "Warm, male" },
  { value: "coral", label: "Coral", description: "Friendly, female" },
  { value: "echo", label: "Echo", description: "Deep, male" },
  { value: "sage", label: "Sage", description: "Calm, female" },
  { value: "shimmer", label: "Shimmer", description: "Bright, female" },
  { value: "verse", label: "Verse", description: "Professional, female" },
]

/**
 * Everything a persona rarely needs, folded away under one "Advanced" trigger.
 * Silent Mode stays visible while on, since it is the only way back out: the
 * other rows hide because none of them apply to a caller who never speaks.
 */
export function PersonaAdvancedFields({
  controller,
}: {
  controller: PersonaFormController
}) {
  const {
    formData,
    isBuiltIn,
    isSpeechDisabled,
    isInSilentMode,
    updateMetadata,
  } = controller
  const metadata = formData.metadata as VoicePersonaConfig
  const holdMusicTimeoutSeconds = (formData.metadata as PersonaMetadata)
    .hold_music_timeout_seconds
  const isHoldMusicTimeoutEnabled = holdMusicTimeoutSeconds != null

  return (
    <Accordion
      type="single"
      collapsible
      className="rounded-md border border-coval-border bg-surface-default"
    >
      <AccordionItem value="advanced" className="border-b-0">
        <AccordionTrigger className="px-6 py-5 text-sm font-semibold">
          <span className="flex items-center gap-2">
            <Settings2 className="size-4 shrink-0" />
            Advanced
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-6">
          <div className="space-y-1">
            <SettingRow
              tooltipIcon="info"
              label="Silent Mode"
              tooltip="The persona stays completely silent for the whole conversation. Use it to test how your agent handles a non-responsive caller."
            >
              {(controlId) => (
                <Switch
                  id={controlId}
                  checked={isInSilentMode}
                  onCheckedChange={(checked) =>
                    updateMetadata(
                      checked
                        ? { silent_mode: true, conversation_initiator: "agent" }
                        : { silent_mode: false },
                    )
                  }
                  disabled={isBuiltIn}
                />
              )}
            </SettingRow>

            <SettingRow
              tooltipIcon="info"
              label="Who Speaks First"
              tooltip="Who opens the call. If the agent waits and your agent also waits for the caller, the conversation will be silent."
            >
              <Tabs
                value={metadata.conversation_initiator ?? "agent"}
                onValueChange={(selected) =>
                  updateMetadata({
                    conversation_initiator:
                      selected as VoicePersonaConfig["conversation_initiator"],
                  })
                }
              >
                <TabsList variant="pill" aria-label="Conversation Initiator">
                  <TabsTrigger
                    value="agent"
                    variant="pill"
                    disabled={isSpeechDisabled}
                    className="px-4"
                  >
                    Agent
                  </TabsTrigger>
                  <TabsTrigger
                    value="persona"
                    variant="pill"
                    disabled={isSpeechDisabled}
                    className="px-4"
                  >
                    Persona
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </SettingRow>

            <SettingRow
              tooltipIcon="info"
              label="Multi-Language STT"
              tooltip='Transcribe speech across multiple languages (e.g. "For English press one, Para español presione dos"). Off by default as it may reduce quality for mono-lingual simulations in some languages.'
            >
              {(controlId) => (
                <Switch
                  id={controlId}
                  checked={
                    (formData.metadata as Record<string, unknown>)
                      ?.multi_language_stt === true
                  }
                  onCheckedChange={(checked) =>
                    updateMetadata({
                      multi_language_stt: checked,
                    } as Partial<PersonaConfig>)
                  }
                  disabled={isSpeechDisabled}
                />
              )}
            </SettingRow>

            <SettingRow
              tooltipIcon="info"
              label="Hold Music Timeout"
              tooltip="Disconnect when no speech is detected for this duration. Useful for ending calls quickly after transfers to hold queues."
            >
              {(controlId) => (
                <>
                  {isHoldMusicTimeoutEnabled && (
                    <Input
                      aria-label="Hold music timeout in seconds"
                      type="text"
                      inputMode="numeric"
                      value={String(
                        holdMusicTimeoutSeconds ??
                          HOLD_MUSIC_TIMEOUT_DEFAULT_SECONDS,
                      )}
                      onChange={(event) => {
                        if (!/^\d*$/.test(event.target.value)) {
                          return
                        }
                        const nextValue = parseInt(event.target.value, 10)
                        if (!isNaN(nextValue)) {
                          updateMetadata({
                            hold_music_timeout_seconds: Math.min(
                              HOLD_MUSIC_TIMEOUT_MAX_SECONDS,
                              Math.max(
                                HOLD_MUSIC_TIMEOUT_MIN_SECONDS,
                                nextValue,
                              ),
                            ),
                          })
                        }
                      }}
                      disabled={isSpeechDisabled}
                      className="w-24"
                      endAdornment={
                        <span className="text-xs text-coval-text-muted">
                          sec
                        </span>
                      }
                    />
                  )}
                  <Switch
                    id={controlId}
                    checked={isHoldMusicTimeoutEnabled}
                    onCheckedChange={(checked) =>
                      updateMetadata({
                        hold_music_timeout_seconds: checked
                          ? HOLD_MUSIC_TIMEOUT_DEFAULT_SECONDS
                          : null,
                      })
                    }
                    disabled={isSpeechDisabled}
                  />
                </>
              )}
            </SettingRow>

            <SettingRow
              tooltipIcon="info"
              label="Interruption Rate"
              tooltip="How often the persona will interrupt your agent."
            >
              <Select
                value={metadata.interruption_rate || "NONE"}
                onValueChange={(selected) =>
                  updateMetadata({
                    interruption_rate:
                      selected as VoicePersonaConfig["interruption_rate"],
                  })
                }
                disabled={isSpeechDisabled}
              >
                <SelectTrigger aria-label="Interruption Rate" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERRUPTION_RATE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow
              tooltipIcon="info"
              label="Phone Number"
              tooltip={
                <>
                  The number the persona calls from.{" "}
                  <a
                    href={CALLER_PHONE_NUMBER_DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline"
                  >
                    Learn more
                  </a>
                </>
              }
            >
              <div className="w-64">
                <MultiPhoneConfig
                  modelType={SimulatorType.VOICE}
                  metadata={metadata}
                  onMetadataChange={(nextMetadata) =>
                    !isBuiltIn && updateMetadata(nextMetadata)
                  }
                  disabled={isSpeechDisabled}
                  showIcon={false}
                />
              </div>
            </SettingRow>

            <SettingRow
              tooltipIcon="info"
              label="OpenAI Agent Voice"
              tooltip="Only used when this persona runs against an OpenAI Realtime endpoint simulation."
            >
              <Select
                value={
                  (formData.metadata as OpenAIRealtimePersonaConfig).voice ?? ""
                }
                onValueChange={(selectedVoice) =>
                  updateMetadata({
                    voice: selectedVoice as OpenAIRealtimeVoice,
                  } as Partial<PersonaConfig>)
                }
                disabled={isSpeechDisabled}
              >
                <SelectTrigger
                  aria-label="Select OpenAI Realtime Voice"
                  className="w-64"
                >
                  <SelectValue placeholder="Select a voice" />
                </SelectTrigger>
                <SelectContent>
                  {OPENAI_REALTIME_VOICES.map((voice) => (
                    <SelectItem
                      key={voice.value}
                      value={voice.value}
                      description={voice.description}
                    >
                      {voice.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingRow>

            <PersonaTextConfigurationField
              value={
                (formData.metadata as TextPersonaConfig).custom_persona_data ||
                ""
              }
              onChange={(custom_persona_data) =>
                updateMetadata({
                  custom_persona_data,
                } as Partial<PersonaConfig>)
              }
              disabled={isSpeechDisabled}
            />

            <PersonaInitializationParametersField
              value={
                (formData.metadata as TextPersonaConfig)
                  .initialization_parameters || {}
              }
              onChange={(initialization_parameters) =>
                updateMetadata({
                  initialization_parameters,
                } as Partial<PersonaConfig>)
              }
              disabled={isSpeechDisabled}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
